import type { ButtonHTMLAttributes, ReactNode } from 'react';

import './chip.css';

export type ChipState = 'Default' | 'choice' | 'State3' | 'State4' | 'State5';

type ChipNativeProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'style' | 'type'
>;

type RouteChipProps = ChipNativeProps & {
  state: 'Default' | 'choice';
  from: string;
  to: string;
};

type PetChipProps = ChipNativeProps & {
  state: 'State3' | 'State4';
  label: string;
  icon?: ReactNode;
};

type WaitingChipProps = ChipNativeProps & {
  state: 'State5';
  title: string;
  description: string;
};

export type ChipProps = RouteChipProps | PetChipProps | WaitingChipProps;

const FIGMA_NODE: Record<ChipState, string> = {
  Default: '124:33065',
  choice: '124:33066',
  State3: '124:33375',
  State4: '124:33401',
  State5: '124:33587',
};

interface ChipRootProps extends ChipNativeProps {
  state: ChipState;
  body: ReactNode;
}

function ChipRoot({ state, body, className = '', ...props }: ChipRootProps) {
  const classes = ['lovo-chip', `lovo-chip--${state}`, className].filter(Boolean).join(' ');

  return (
    <button
      {...props}
      className={classes}
      data-figma-node={FIGMA_NODE[state]}
      data-figma-variant={`State=${state}`}
      type="button"
    >
      {body}
    </button>
  );
}

export function Chip(props: ChipProps) {
  if (props.state === 'Default' || props.state === 'choice') {
    const { state, from, to, ...buttonProps } = props;

    return (
      <ChipRoot
        {...buttonProps}
        state={state}
        body={
          <>
            <span className="lovo-chip__label">{from}</span>
            <span className="lovo-chip__icon lovo-chip__icon--route" aria-hidden="true" />
            <span className="lovo-chip__label">{to}</span>
          </>
        }
      />
    );
  }

  if (props.state === 'State3' || props.state === 'State4') {
    const { state, label, icon, ...buttonProps } = props;

    return (
      <ChipRoot
        {...buttonProps}
        state={state}
        body={
          <>
            {icon ?? <span className="lovo-chip__icon lovo-chip__icon--dog" aria-hidden="true" />}
            <span className="lovo-chip__label">{label}</span>
          </>
        }
      />
    );
  }

  if (props.state === 'State5') {
    const { state, title, description, ...buttonProps } = props;

    return (
      <ChipRoot
        {...buttonProps}
        state={state}
        body={
          <>
            <span className="lovo-chip__label">{title}</span>
            <span className="lovo-chip__description">{description}</span>
          </>
        }
      />
    );
  }

  return null;
}
